import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getUser } from '@/utils/auth';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { 
  Key, Shield, Calendar, ArrowRight, CheckCircle, Phone, 
  MessageCircle, Building2, Wrench, Users 
} from 'lucide-react';

const services = [
  {
    id: 'full_management',
    title: 'Full Management',
    icon: Shield,
    description: 'Comprehensive property management including tenant screening, rent collection, maintenance, and legal compliance.',
    features: [
      'Tenant screening & verification',
      'Rent collection & accounting',
      'Property maintenance coordination',
      'Legal compliance & documentation',
      'Regular property inspections',
      '24/7 tenant support'
    ]
  },
  {
    id: 'rental_only',
    title: 'Rental Services',
    icon: Key,
    description: 'Tenant sourcing, agreement preparation, and rent management for your rental property.',
    features: [
      'Tenant sourcing & screening',
      'Rental agreement preparation',
      'Security deposit management',
      'Rent collection',
      'Basic property support'
    ]
  },
  {
    id: 'maintenance_only',
    title: 'Maintenance Only',
    icon: Calendar,
    description: 'Regular property maintenance, repairs, and upkeep services to keep your property in top condition.',
    features: [
      'Regular property inspections',
      'Preventive maintenance',
      'Emergency repairs',
      'Vendor management',
      'Monthly maintenance reports'
    ]
  }
];

const PropertyManagement = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [requestId, setRequestId] = useState(null);
  const [formData, setFormData] = useState({
    owner_name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    property_address: '',
    property_type: 'residential',
    service_type: 'full_management',
    rental_status: 'vacant',
    additional_notes: ''
  });

  const handleLearnMore = async (serviceId) => {
    // Navigate to request form with pre-filled service type
    setSelectedService(serviceId);
    setFormData(prev => ({ ...prev, service_type: serviceId }));
    setActiveTab('request');
    
    // Scroll to top of the form section
    setTimeout(() => {
      document.getElementById('request-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    
    const serviceName = services.find(s => s.id === serviceId)?.title || serviceId;
    toast.info(`Selected ${serviceName}. Please fill in your details below.`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      // Create lead in CRM - this triggers WhatsApp notification in background via backend
      const leadResponse = await apiClient.post('/leads/property-management', {
        name: formData.owner_name,
        phone: formData.phone,
        service_type: formData.service_type,
        email: formData.email || null,
        city: null,
        property_address: formData.property_address,
        message: formData.additional_notes || null
      });
      
      const reqId = leadResponse.data.request_id;
      setRequestId(reqId);
      
      // Also submit the original form if user is logged in
      if (user) {
        try {
          await apiClient.post('/property-management/request', formData);
        } catch (e) {
          // Ignore if original endpoint fails
        }
      }
      
      const serviceName = services.find(s => s.id === formData.service_type)?.title || formData.service_type;
      toast.success(`Request submitted successfully! Your Request ID: ${reqId}. Our team will contact you within 24 hours.`);
      
      // Note: WhatsApp notification is triggered automatically via backend CRM adapter
      // No need to open WhatsApp window - this happens behind the scenes
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="property-management-page">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-primary/5 via-white to-gray-50">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Building2 className="w-4 h-4" /> Property Management
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="management-title">
            Professional Property Management
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Let us handle your property while you enjoy hassle-free ownership. 
            From tenant management to maintenance - we&apos;ve got you covered.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b sticky top-16 bg-white z-40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex gap-0">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-8 py-4 font-semibold text-lg transition-all border-b-4 ${
                activeTab === 'overview' 
                  ? 'border-primary text-primary bg-primary/5' 
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid="tab-overview"
            >
              Our Services
            </button>
            <button
              onClick={() => setActiveTab('request')}
              className={`px-8 py-4 font-semibold text-lg transition-all border-b-4 ${
                activeTab === 'request' 
                  ? 'border-primary text-primary bg-primary/5' 
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid="tab-request"
            >
              Request Service
            </button>
          </div>
        </div>
      </section>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <section className="py-16" data-testid="overview-content">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-8">
              {services.map((service) => (
                <div 
                  key={service.id} 
                  className="bg-white border-2 border-gray-100 hover:border-primary/30 rounded-2xl p-8 transition-all hover:shadow-xl group"
                >
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                    <service.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className="w-full rounded-full group-hover:bg-primary"
                    variant={selectedService === service.id ? "default" : "outline"}
                    onClick={() => handleLearnMore(service.id)}
                    data-testid={`learn-more-${service.id}`}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ))}
            </div>

            {/* What's Included Section */}
            <div className="mt-16 bg-gray-50 rounded-2xl p-8 md:p-12">
              <h3 className="text-2xl font-semibold mb-8 text-center">Why Choose ZenStudios Property Management?</h3>
              <div className="grid md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-primary" />
                  </div>
                  <h4 className="font-semibold mb-2">Expert Team</h4>
                  <p className="text-sm text-gray-600">Experienced property managers handling your asset</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-primary" />
                  </div>
                  <h4 className="font-semibold mb-2">Legal Compliance</h4>
                  <p className="text-sm text-gray-600">All documentation handled professionally</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Wrench className="w-8 h-8 text-primary" />
                  </div>
                  <h4 className="font-semibold mb-2">Quick Maintenance</h4>
                  <p className="text-sm text-gray-600">Prompt repairs and regular upkeep</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-8 h-8 text-primary" />
                  </div>
                  <h4 className="font-semibold mb-2">24/7 Support</h4>
                  <p className="text-sm text-gray-600">Always available for emergencies</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Request Tab Content */}
      {activeTab === 'request' && (
        <section className="py-16" data-testid="request-content" id="request-section">
          <div className="max-w-3xl mx-auto px-6">
            {requestId && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-800">Your Request ID: <strong>{requestId}</strong></span>
              </div>
            )}
            
            <div className="bg-white border rounded-2xl p-8 md:p-12 shadow-lg">
              <h2 className="text-2xl font-semibold mb-2">Request Property Management Service</h2>
              <p className="text-gray-600 mb-8">Fill in your details and we&apos;ll get back to you within 24 hours</p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      className="rounded-xl mt-1"
                      value={formData.owner_name}
                      onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                      placeholder="Enter your name"
                      required
                      data-testid="mgmt-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      className="rounded-xl mt-1"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="your@email.com"
                      required
                      data-testid="mgmt-email-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      className="rounded-xl mt-1"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+91 98765 43210"
                      required
                      data-testid="mgmt-phone-input"
                    />
                  </div>
                  <div>
                    <Label>Property Type *</Label>
                    <Select value={formData.property_type} onValueChange={(value) => setFormData({...formData, property_type: value})}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Residential</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Property Address *</Label>
                  <Input
                    className="rounded-xl mt-1"
                    value={formData.property_address}
                    onChange={(e) => setFormData({...formData, property_address: e.target.value})}
                    placeholder="Full property address"
                    required
                    data-testid="mgmt-address-input"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label>Service Type *</Label>
                    <Select value={formData.service_type} onValueChange={(value) => setFormData({...formData, service_type: value})}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_management">Full Management</SelectItem>
                        <SelectItem value="rental_only">Rental Services Only</SelectItem>
                        <SelectItem value="maintenance_only">Maintenance Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Current Rental Status *</Label>
                    <Select value={formData.rental_status} onValueChange={(value) => setFormData({...formData, rental_status: value})}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vacant">Vacant</SelectItem>
                        <SelectItem value="occupied">Occupied</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Additional Notes</Label>
                  <Textarea
                    className="rounded-xl mt-1"
                    rows={4}
                    value={formData.additional_notes}
                    onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
                    placeholder="Any specific requirements or concerns..."
                    data-testid="mgmt-notes-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full rounded-full py-6 text-lg"
                  disabled={loading}
                  data-testid="submit-mgmt-request-btn"
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </Button>
                
                <p className="text-center text-sm text-gray-500">
                  Our team will contact you within 24 hours to discuss your requirements.
                </p>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Have Questions?</h2>
          <p className="text-white/80 mb-8">Our property management experts are here to help you make the right decision</p>
          <Button 
            size="lg"
            className="rounded-full bg-white text-primary hover:bg-gray-100 px-8"
            onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%20have%20questions%20about%20property%20management%20services.', '_blank')}
          >
            <Phone className="w-5 h-5 mr-2" />
            Talk to an Expert
          </Button>
        </div>
      </section>
    </div>
  );
};

export default PropertyManagement;
